'use client';

import { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Circle, ArrowRight } from 'lucide-react';
import type { NoteTakerAnalysis, TopicDefinition, PhaseId } from '@/lib/interview/types';

// Phase display configuration
const PHASE_DISPLAY: Record<PhaseId, { name: string; description: string }> = {
  discovery: {
    name: 'Kennismaking',
    description: 'Wie je bent en wat je zoekt',
  },
  deep_dive: {
    name: 'Verdieping',
    description: 'Uitdagingen en kansen verkennen',
  },
  summary: {
    name: 'Afsluiting',
    description: 'Samenvatten en afstemmen',
  },
};

interface ContextDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  analysis: NoteTakerAnalysis | null;
  topics: TopicDefinition[];
  currentTopicId?: string | null;
  currentPhase?: PhaseId | null;
}

export function ContextDrawer({
  isOpen,
  onClose,
  analysis,
  topics,
  currentTopicId,
  currentPhase,
}: ContextDrawerProps) {
  // Determine if we're in phase-based mode
  const isPhaseBasedMode = !!currentPhase || !!analysis?.phaseAssessment;
  // Close on escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  // Get topic status - simplified (no percentages exposed) - legacy mode
  const getTopicStatus = (topicId: string) => {
    const coverage = analysis?.coverage?.[topicId] ?? 0;
    if (coverage >= 80) return 'completed';
    if (coverage > 0 || topicId === currentTopicId) return 'in_progress';
    return 'pending';
  };

  // Get phase status - phase-based mode
  const getPhaseStatus = (phaseId: PhaseId): 'pending' | 'in_progress' | 'completed' => {
    const activePhase = analysis?.phaseAssessment?.currentPhase || currentPhase;
    const phaseOrder: PhaseId[] = ['discovery', 'deep_dive', 'summary'];
    const activeIndex = activePhase ? phaseOrder.indexOf(activePhase) : 0;
    const phaseIndex = phaseOrder.indexOf(phaseId);

    if (phaseIndex < activeIndex) return 'completed';
    if (phaseIndex === activeIndex) return 'in_progress';
    return 'pending';
  };

  // Get phase quality indicator
  const getPhaseQuality = (): string | null => {
    const quality = analysis?.phaseAssessment?.phaseQuality;
    if (!quality) return null;
    const qualityLabels = {
      shallow: 'Oppervlakkig',
      adequate: 'Goed',
      rich: 'Uitgebreid',
    };
    return qualityLabels[quality];
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-off-black/30 backdrop-blur-sm z-40"
            onClick={onClose}
          />

          {/* Drawer - Mobile: bottom sheet, Desktop: right panel */}
          <motion.div
            initial={{
              y: '100%',
              x: 0,
            }}
            animate={{
              y: 0,
              x: 0,
            }}
            exit={{
              y: '100%',
              x: 0,
            }}
            transition={{
              type: 'spring',
              damping: 30,
              stiffness: 300,
            }}
            className="
              fixed z-50 bg-white overflow-hidden

              /* Mobile: bottom sheet */
              inset-x-0 bottom-0 max-h-[85vh] rounded-t-3xl

              /* Desktop: right panel */
              md:inset-y-0 md:right-0 md:left-auto md:w-[360px] md:max-h-full md:rounded-l-3xl md:rounded-tr-none
            "
            style={{
              boxShadow: '0 -10px 40px rgba(0,0,0,0.15)',
            }}
          >
            {/* Handle bar (mobile only) */}
            <div className="md:hidden flex justify-center pt-3 pb-2">
              <div className="w-10 h-1 bg-off-black/20 rounded-full" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-off-black/10">
              <div>
                <h2 className="font-display text-2xl font-light text-off-black">
                  {isPhaseBasedMode ? 'Gespreksflow' : 'Gespreksagenda'}
                </h2>
                <p className="text-sm text-off-black/50">
                  {isPhaseBasedMode
                    ? 'De fasen van ons gesprek'
                    : 'Onderwerpen die we samen verkennen'}
                </p>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-off-black/5 transition-colors"
                aria-label="Paneel sluiten"
              >
                <X className="w-5 h-5 text-off-black/50" />
              </button>
            </div>

            {/* Content */}
            <div className="overflow-y-auto max-h-[calc(85vh-120px)] md:max-h-[calc(100vh-120px)] px-6 py-4 space-y-6">

              {isPhaseBasedMode ? (
                /* Phase-based view - 3 phases */
                <section>
                  <div className="space-y-3">
                    {(['discovery', 'deep_dive', 'summary'] as PhaseId[]).map((phaseId, index) => {
                      const status = getPhaseStatus(phaseId);
                      const phaseInfo = PHASE_DISPLAY[phaseId];
                      const isActive = status === 'in_progress';
                      const quality = isActive ? getPhaseQuality() : null;

                      return (
                        <motion.div
                          key={phaseId}
                          layout
                          className={`
                            relative flex items-start gap-4 p-4 rounded-xl transition-colors
                            ${isActive ? 'bg-teal/10 border border-teal/20' : 'bg-off-black/[0.02]'}
                          `}
                        >
                          {/* Phase number + status icon */}
                          <div className="flex flex-col items-center gap-1">
                            <div className={`
                              w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0
                              ${status === 'completed' ? 'bg-teal text-white' : ''}
                              ${status === 'in_progress' ? 'bg-teal/20 text-teal' : ''}
                              ${status === 'pending' ? 'bg-off-black/10 text-off-black/30' : ''}
                            `}>
                              {status === 'completed' && <Check className="w-4 h-4" />}
                              {status === 'in_progress' && <ArrowRight className="w-4 h-4" />}
                              {status === 'pending' && <span className="text-xs font-medium">{index + 1}</span>}
                            </div>
                            {/* Connecting line (except last item) */}
                            {index < 2 && (
                              <div className={`
                                w-0.5 h-6 -mb-2
                                ${status === 'completed' ? 'bg-teal/40' : 'bg-off-black/10'}
                              `} />
                            )}
                          </div>

                          {/* Phase info */}
                          <div className="flex-1 min-w-0 pt-1">
                            <span className={`
                              block text-sm font-medium
                              ${status === 'completed' ? 'text-off-black/50' : ''}
                              ${status === 'in_progress' ? 'text-off-black font-semibold' : ''}
                              ${status === 'pending' ? 'text-off-black/70' : ''}
                            `}>
                              {phaseInfo.name}
                            </span>
                            <span className="block text-xs text-off-black/40 mt-0.5">
                              {phaseInfo.description}
                            </span>
                            {/* Quality indicator for active phase */}
                            {quality && (
                              <span className="inline-block mt-2 text-xs px-2 py-0.5 rounded-full bg-teal/10 text-teal">
                                {quality}
                              </span>
                            )}
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </section>
              ) : (
                /* Legacy topic-based view */
                <section>
                  <div className="space-y-2">
                    {topics.map((topic) => {
                      const status = getTopicStatus(topic.id);
                      const isCurrent = topic.id === currentTopicId || status === 'in_progress';

                      return (
                        <motion.div
                          key={topic.id}
                          layout
                          className={`
                            flex items-center gap-3 p-3 rounded-xl transition-colors
                            ${isCurrent && status === 'in_progress' ? 'bg-teal/10 border border-teal/20' : 'bg-off-black/[0.02]'}
                          `}
                        >
                          {/* Status icon */}
                          <div className={`
                            w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0
                            ${status === 'completed' ? 'bg-teal text-white' : ''}
                            ${status === 'in_progress' ? 'bg-teal/20 text-teal' : ''}
                            ${status === 'pending' ? 'bg-off-black/10 text-off-black/30' : ''}
                          `}>
                            {status === 'completed' && <Check className="w-3.5 h-3.5" />}
                            {status === 'in_progress' && <ArrowRight className="w-3.5 h-3.5" />}
                            {status === 'pending' && <Circle className="w-3 h-3" />}
                          </div>

                          {/* Topic info */}
                          <div className="flex-1 min-w-0">
                            <span className={`
                              text-sm font-medium
                              ${status === 'completed' ? 'text-off-black/50 line-through' : ''}
                              ${status === 'in_progress' ? 'text-off-black font-semibold' : ''}
                              ${status === 'pending' ? 'text-off-black/70' : ''}
                            `}>
                              {topic.name}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </section>
              )}

              {/* Reassuring message */}
              <div className="pt-4 border-t border-off-black/5">
                <p className="text-sm text-off-black/40 text-center">
                  Neem je tijd - er is geen haast.<br />
                  We behandelen wat relevant is voor jullie.
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
